import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Phone, Facebook, X, ChevronDown } from "lucide-react";
import { site, navMain, navFlat } from "../data/mock";
import Img from "./Img";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [dropOpen, setDropOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setDropOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const isProductActive = navMain
    .find((n) => n.children)
    ?.children.some((c) => location.pathname === c.to);

  return (
    <>
      <div className="hidden bg-[#2f3a33] text-[11px] tracking-[0.14em] text-white/80 md:block">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between px-6 py-2">
          <span className="uppercase">
            Eat clean · Bánh trung thu · Bánh kem · Lớp dạy làm bánh
          </span>
          <div className="flex items-center gap-6">
            <a
              href={site.phoneHref}
              className="flex items-center gap-2 transition-colors duration-300 hover:text-white"
            >
              <Phone size={13} strokeWidth={2} />
              {site.phone}
            </a>
            <a
              href={site.facebook}
              target="_blank"
              rel="noreferrer"
              aria-label="Facebook Ann Home"
              className="transition-colors duration-300 hover:text-white"
            >
              <Facebook size={13} strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>

      <header
        className={`sticky top-0 z-50 w-full border-b transition-all duration-500 ${
          scrolled
            ? "border-black/5 bg-white/95 shadow-[0_6px_28px_-18px_rgba(0,0,0,0.35)] backdrop-blur-md"
            : "border-transparent bg-white"
        }`}
      >
        <div className="mx-auto max-w-[1240px] px-5">
          {/* desktop */}
          <div
            className="hidden items-center justify-between lg:flex"
            style={{ height: scrolled ? 76 : 92, transition: "height .45s ease" }}
          >
            <Link to="/" aria-label="Ann Home - trang chủ" className="flex items-center gap-3">
              <Img
                src={site.logo}
                alt="Logo Ann Home"
                width={320}
                height={320}
                className={`w-auto transition-all duration-500 ${
                  scrolled ? "h-[50px]" : "h-[64px]"
                }`}
              />
              <span className="leading-tight">
                <span className="block text-[17px] font-bold leading-tight tracking-[0.14em] text-[#2f3a33]">
                  ANN HOME
                </span>
                <span className="mt-1 block text-[10px] uppercase leading-tight tracking-[0.2em] text-[#657827]">
                  Căn bếp của những điều tử tế
                </span>
              </span>
            </Link>

            <nav aria-label="Điều hướng chính">
              <ul className="flex items-center gap-8">
                {navMain.map((n) =>
                  n.children ? (
                    <li
                      key={n.label}
                      className="relative"
                      onMouseEnter={() => setDropOpen(true)}
                      onMouseLeave={() => setDropOpen(false)}
                    >
                      <button
                        data-testid="products-menu-trigger"
                        type="button"
                        aria-haspopup="true"
                        aria-expanded={dropOpen}
                        onClick={() => setDropOpen((v) => !v)}
                        className={`inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.16em] transition-colors duration-300 hover:text-[#148456] ${
                          isProductActive ? "text-[#148456]" : "text-[#3f3f3f]"
                        }`}
                      >
                        {n.label}
                        <ChevronDown
                          size={13}
                          className={`transition-transform duration-300 ${
                            dropOpen ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <div
                        className={`absolute left-1/2 top-full z-50 -translate-x-1/2 pt-4 transition-all duration-300 ${
                          dropOpen
                            ? "pointer-events-auto visible translate-y-0 opacity-100"
                            : "pointer-events-none invisible -translate-y-1.5 opacity-0"
                        }`}
                      >
                        <ul
                          data-testid="products-menu-content"
                          className="w-[340px] rounded-md border border-black/5 bg-white p-3 shadow-[0_24px_54px_-24px_rgba(0,0,0,0.35)]"
                        >
                          {n.children.map((c) => (
                            <li key={c.to}>
                              <Link
                                to={c.to}
                                className="block rounded-sm px-4 py-3 transition-colors duration-300 hover:bg-[#f6faf7]"
                              >
                                <span className="block text-[13.5px] font-bold text-[#2f3a33]">
                                  {c.label}
                                </span>
                                <span className="mt-1 block text-[12.5px] font-light leading-snug text-[#8a8a82]">
                                  {c.desc}
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </li>
                  ) : (
                    <li key={n.to}>
                      <NavLink
                        to={n.to}
                        end={n.to === "/"}
                        className={({ isActive }) =>
                          `ah-nav-link text-[12px] font-bold uppercase tracking-[0.16em] transition-colors duration-300 ${
                            isActive
                              ? "is-active text-[#148456]"
                              : "text-[#3f3f3f] hover:text-[#148456]"
                          }`
                        }
                      >
                        {n.label}
                      </NavLink>
                    </li>
                  )
                )}
              </ul>
            </nav>

            <a
              href={site.zalo}
              target="_blank"
              rel="noreferrer"
              className="ah-btn ah-btn-solid !px-6 !py-3"
            >
              Đặt hàng
            </a>
          </div>

          {/* mobile */}
          <div className="flex h-[72px] items-center justify-between lg:hidden">
            <Link to="/" aria-label="Ann Home - trang chủ" className="flex items-center gap-2.5">
              <Img src={site.logo} alt="Logo Ann Home" width={320} height={320} className="h-[44px] w-auto" />
              <span className="text-[14px] font-bold tracking-[0.12em] text-[#2f3a33]">
                ANN HOME
              </span>
            </Link>
            <button
              aria-label="Mở menu"
              aria-expanded={open}
              data-testid="mobile-menu-trigger"
              onClick={() => setOpen(true)}
              className="rounded-sm border border-black/10 p-2.5 text-[#3f3f3f] transition-colors duration-300 hover:border-[#148456] hover:text-[#148456]"
            >
              <Menu size={20} />
            </button>
          </div>
        </div>
      </header>

      {/* mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] bg-black/45 transition-opacity duration-300 lg:hidden ${
          open ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Menu điều hướng"
        data-testid="mobile-menu-panel"
        className={`fixed right-0 top-0 z-[61] h-full w-[300px] overflow-y-auto bg-white shadow-xl transition-transform duration-300 ease-out lg:hidden ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between border-b border-black/5 px-6 py-5">
          <Img src={site.logo} alt="Logo Ann Home" width={320} height={320} className="h-11 w-auto" />
          <button
            onClick={() => setOpen(false)}
            aria-label="Đóng menu"
            data-testid="mobile-menu-close"
            className="text-[#3f3f3f] transition-colors hover:text-[#148456]"
          >
            <X size={20} />
          </button>
        </div>
        <ul className="flex flex-col px-6 py-3">
          {navFlat.map((n) => (
            <li key={n.to} className="border-b border-black/5 last:border-0">
              <NavLink
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `block py-3.5 text-[12px] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    isActive ? "text-[#148456]" : "text-[#3f3f3f] hover:text-[#148456]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="px-6 pb-8 pt-3">
          <a href={site.phoneHref} className="ah-btn ah-btn-solid w-full">
            <Phone size={14} />{` ${site.phone}`}
          </a>
        </div>
      </aside>
    </>
  );
}
